import { customAlphabet } from 'nanoid';
import { PASSWORD_RULES, PASSWORD_ERROR_MESSAGES } from '@etudescarre/constants';

// src/nil.ts
var isNil = (arg) => {
  return arg === null || arg === void 0;
};
var isNotNil = (arg) => {
  return arg !== null && arg !== void 0;
};

// src/array.ts
var isEmptyArray = (arg) => {
  return isNil(arg) || arg.length === 0;
};
var nanoid = customAlphabet(
  "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz"
);
var prefixes = {
  org: "org",
  user: "user",
  member: "member",
  invite: "invite",
  test: "test"
  // <-- for tests only
};
function newId(prefix) {
  return [prefixes[prefix], nanoid(16)].filter(Boolean).join("_");
}

// src/list-param.ts
function joinListParam(values) {
  return values.map((value) => String(value).replace(/[\\,]/g, "\\$&")).join(",");
}
function splitListParam(param) {
  return (param?.match(/(?:\\.|[^,\\])+/g) ?? []).map(
    (value) => value.replace(/\\(.)/g, "$1")
  );
}
function validatePasswordStrength(password) {
  const errors = [];
  if (password.length < PASSWORD_RULES.minLength) {
    errors.push(PASSWORD_ERROR_MESSAGES.tooShort);
  }
  if (PASSWORD_RULES.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push(PASSWORD_ERROR_MESSAGES.missingUppercase);
  }
  if (PASSWORD_RULES.requireLowercase && !/[a-z]/.test(password)) {
    errors.push(PASSWORD_ERROR_MESSAGES.missingLowercase);
  }
  if (PASSWORD_RULES.requireNumber && !/\d/.test(password)) {
    errors.push(PASSWORD_ERROR_MESSAGES.missingNumber);
  }
  return errors;
}

// src/proxy-class.ts
var proxyClass = () => {
  return class {
    constructor() {
      return new Proxy(this, {
        get: (target, prop, receiver) => {
          if (prop in target) {
            return Reflect.get(target, prop, receiver);
          }
          const client = this.client();
          if (prop in client) {
            return Reflect.get(client, prop, receiver);
          }
        }
      });
    }
    client() {
      throw new Error("not implemented");
    }
  };
};

// src/string.ts
var isValidEmail = (value) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);
var capitalize = (value) => {
  const trimmed = value.trim();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};
var capitalizeFirstLetter = (value) => {
  const trimmed = value.trim();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};
var capitalizeKeepingAcronyms = (value) => {
  const trimmed = value.trim();
  const isAllCapsPhrase = trimmed === trimmed.toUpperCase() && /\s/.test(trimmed);
  return isAllCapsPhrase ? capitalize(trimmed) : capitalizeFirstLetter(trimmed);
};
var compareLotNumbers = (a, b) => (a ?? "").localeCompare(b ?? "", void 0, { numeric: true });

// src/yard-status.ts
function deriveYardStatus(yard, now = /* @__PURE__ */ new Date()) {
  const isOff = yard.isLocked;
  if (isNil(yard.comissioningDate)) {
    return { status: "preparation", isOff };
  }
  if (isNil(yard.expirationDate)) {
    return { status: "en_service", isOff };
  }
  const expiration = new Date(yard.expirationDate);
  const archiveDays = yard.beforeArchivingDays ?? 0;
  const archiveEnd = new Date(expiration);
  archiveEnd.setDate(archiveEnd.getDate() + archiveDays);
  if (now <= expiration) {
    return { status: "en_service", isOff };
  }
  if (now <= archiveEnd) {
    return { status: "archive", isOff };
  }
  return { status: "cloture", isOff };
}
function isYardInPreparation(yard, now = /* @__PURE__ */ new Date()) {
  return deriveYardStatus(yard, now).status === "preparation";
}
var YARD_VISIBILITY_GRACE_DAYS = 90;
function isYardVisibleForSelection(yard, now = /* @__PURE__ */ new Date()) {
  if (isNil(yard.comissioningDate)) {
    return false;
  }
  if (isNil(yard.expirationDate)) {
    return true;
  }
  const expiration = new Date(yard.expirationDate);
  const archiveDays = yard.beforeArchivingDays ?? 0;
  const archiveEnd = new Date(expiration);
  archiveEnd.setDate(archiveEnd.getDate() + archiveDays);
  const visibilityEnd = new Date(archiveEnd);
  visibilityEnd.setDate(visibilityEnd.getDate() + YARD_VISIBILITY_GRACE_DAYS);
  return now <= visibilityEnd;
}

export { YARD_VISIBILITY_GRACE_DAYS, capitalize, capitalizeFirstLetter, capitalizeKeepingAcronyms, compareLotNumbers, deriveYardStatus, isEmptyArray, isNil, isNotNil, isValidEmail, isYardInPreparation, isYardVisibleForSelection, joinListParam, newId, proxyClass, splitListParam, validatePasswordStrength };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map